import React, { useState } from 'react';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { useSnackbar } from 'notistack';
import { formatTime } from '../../../utils/common';
import { Exam, ExamDiploma, PassedParticipantsContext } from '../../../types';
import ButtonRow from '../../../components/ButtonRow';
import ExamRepository from '../repository/ExamRepository';
import { examLineToExamLineDTO } from '../utils';

interface PassedParticipantsProps {
  exam: Exam;
  context: PassedParticipantsContext;
  canEdit: boolean;
  onSave: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    width: 'auto',
  },
  tableHead: {
    color: theme.palette.primary.dark,
    fontFamily: theme.typography.h1.fontFamily,
    fontWeight: theme.typography.h1.fontWeight,
    fontSize: 18,
    padding: 0,
    borderBottom: 0,
    paddingRight: 20,
  },
  tableCell: {
    paddingTop: 0,
    paddingLeft: 0,
    borderBottom: 'none',
  },
}));

const PassedParticipants = (props: PassedParticipantsProps) => {
  const { exam } = props;

  const classes = useStyles();
  const notifications = useSnackbar();

  const [examLines, setExamLines] = useState<ExamDiploma[]>(exam.examLines);

  const handleAmountChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    index: number,
  ) => {
    // persisting the event is necessary in React v16 and lower,
    // otherwise the event can only be called once
    event.persist();
    setExamLines((prev) => {
      const updatedExamLines = [...prev];
      const field = event.target.name as
        | 'numberParticipants'
        | 'numberPassedAccordingExaminer'
        | 'numberPassedAccordingDelegate';
      updatedExamLines[index][field] = parseInt(
        event.target.value as string,
        10,
      );
      return updatedExamLines;
    });
  };

  const handleSubmit = () => {
    new ExamRepository()
      .updatePassedParticipants(
        exam.id,
        props.context,
        examLines.map((examLine: ExamDiploma) =>
          examLineToExamLineDTO(examLine, props.context),
        ),
      )
      .then(() => {
        notifications.enqueueSnackbar(
          'Het aantal geslaagde kandidaten is succesvol opgeslagen!',
          {
            variant: 'success',
          },
        );
        props.onSave();
      })
      .catch(() => {
        notifications.enqueueSnackbar(
          `Er is iets fout gegaan. Controleer de invoer.`,
          {
            variant: 'error',
          },
        );
      });
  };

  return (
    <>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell variant="head" className={classes.tableHead}>
              Diplomatype
            </TableCell>
            <TableCell variant="head" className={classes.tableHead}>
              Starttijd
            </TableCell>
            <TableCell variant="head" className={classes.tableHead}>
              Eindtijd
            </TableCell>
            <TableCell variant="head" className={classes.tableHead}>
              Aantal kandidaten
            </TableCell>
            <TableCell variant="head" className={classes.tableHead}>
              Aantal geslaagd
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {examLines.map((examLine, index) => (
            <TableRow key={examLine.id}>
              <TableCell className={classes.tableCell}>
                {examLine.diplomaType.name}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {formatTime(new Date(examLine.beginTime))}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {formatTime(new Date(examLine.endTime))}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {props.canEdit ? (
                  <TextField
                    type="number"
                    name="numberParticipants"
                    value={examLine.numberParticipants}
                    onChange={(
                      event: React.ChangeEvent<{
                        name?: string;
                        value: unknown;
                      }>,
                    ) => handleAmountChange(event, index)}
                    error={examLine.numberParticipants < 0}
                  />
                ) : (
                  examLine.numberParticipants
                )}
              </TableCell>
              {props.context === PassedParticipantsContext.examiner ? (
                <TableCell className={classes.tableCell}>
                  {props.canEdit ? (
                    <TextField
                      type="number"
                      name="numberPassedAccordingExaminer"
                      value={examLine.numberPassedAccordingExaminer}
                      onChange={(
                        event: React.ChangeEvent<{
                          name?: string;
                          value: unknown;
                        }>,
                      ) => handleAmountChange(event, index)}
                      error={examLine.numberPassedAccordingExaminer < 0}
                    />
                  ) : (
                    examLine.numberPassedAccordingExaminer
                  )}
                </TableCell>
              ) : null}
              {props.context === PassedParticipantsContext.delegate ? (
                <TableCell className={classes.tableCell}>
                  {props.canEdit ? (
                    <TextField
                      type="number"
                      name="numberPassedAccordingDelegate"
                      value={examLine.numberPassedAccordingDelegate}
                      onChange={(
                        event: React.ChangeEvent<{
                          name?: string;
                          value: unknown;
                        }>,
                      ) => handleAmountChange(event, index)}
                      error={examLine.numberPassedAccordingDelegate < 0}
                    />
                  ) : (
                    examLine.numberPassedAccordingDelegate
                  )}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {props.canEdit && (
        <ButtonRow>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Opslaan
          </Button>
        </ButtonRow>
      )}
    </>
  );
};

export default PassedParticipants;
